import React, { useContext } from "react";
import { Box, Button, Card, CardContent, Chip, Divider, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ChairAltIcon from "@mui/icons-material/ChairAlt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupIcon from "@mui/icons-material/Group";
import Banner1 from "../assets/Banner_1.jpeg";
import Banner2 from "../assets/Banner_2.jpeg";
import BannerMain from "../assets/Banner.jpeg";


import DashboardLayout from "../layout/DashboardLayout";
import { AuthContext } from "../auth/AuthContext";

// ✅ Use same datasheet as Events + EventDetails
import { upcomingEventsData } from "../data/upcomingEventsData";

function formatDatePretty(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function fmtDate(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtTime(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

const mockBanners = [
  { id: "b1", title: "Art Classes", image: Banner1 },
  { id: "b2", title: "Weekend Events", image: Banner2 },
//  { id: "b3", title: "Creative Workshops", image: BannerMain }
];


export default function Home() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // ✅ show 3 upcoming events on Home
  const upcomingTop3 = Array.isArray(upcomingEventsData) ? upcomingEventsData.slice(0, 3) : [];

  return (
    <DashboardLayout title="Dashboard">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" },
          gap: 3
        }}
      >
        {/* LEFT */}
        <Box sx={{ minWidth: 0 }}>
          {/* Welcome */}
          <Card
            elevation={0}
            sx={{
              bgcolor: "action.selected",
              borderColor: "divider"
            }}
          >
            <CardContent sx={{ py: 2.25 }}>
              <Typography sx={{ color: "text.primary", fontWeight: 900 }}>
                Welcome back, {user?.fullName || "User"}
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13, color: "text.secondary" }}>
                Here's what's happening at NeherBaari today
              </Typography>
              <Typography sx={{ mt: 0.6, fontSize: 12.5, color: "text.secondary" }}>
                {formatDatePretty(new Date())}
              </Typography>
            </CardContent>
          </Card>

{/* Quick actions */}
<Box sx={{ mt: 3 }}>
  <Box className="sectionHeader">
    <Typography className="sectionHeaderTitle">Quick Actions</Typography>
    <Box className="sectionHeaderLine" />
  </Box>

  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
      gap: 2
    }}
  >
    {[
      {
        icon: (
          <ChairAltIcon
            sx={{ fontSize: 34, color: "#D6AF6E" }}
          />
        ),
        title: "Book a Seat",
        sub: "Browse upcoming activities",
        onClick: () => navigate("/events")
      },
      {
        icon: (
          <CalendarMonthIcon
            sx={{ fontSize: 34, color: "#D6AF6E" }}
          />
        ),
        title: "Book a Meeting Room",
        sub: "View your reservations",
        onClick: () => navigate("/meeting-rooms")
      },
      {
        icon: (
          <GroupIcon
            sx={{ fontSize: 34, color: "#D6AF6E" }}
          />
        ),
        title: "Book the Space for an Event",
        sub: "Manage your plan",
        onClick: () => navigate("/events")
      }
    ].map((a) => (
      <Card
        key={a.title}
        elevation={0}
        sx={{
          cursor: "pointer",
          textAlign: "center",
          py: 3
        }}
        onClick={a.onClick}
      >
        <Box sx={{ mb: 1 }}>{a.icon}</Box>

        <Typography
          sx={{ fontSize: 13.5, color: "text.primary", fontWeight: 900 }}
        >
          {a.title}
        </Typography>

        <Typography
          sx={{
            mt: 0.6,
            fontSize: 12.5,
            color: "text.secondary"
          }}
        >
          {a.sub}
        </Typography>
      </Card>
    ))}
  </Box>
</Box>


{/* ✅ Upcoming from datasheet */}
<Box sx={{ mt: 3 }}>
  <Box className="sectionHeader">
    <Typography className="sectionHeaderTitle">Upcoming Activities & Events</Typography>
    <Box className="sectionHeaderLine" />
  </Box>

  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
      gap: 2
    }}
  >
    {upcomingTop3.length === 0 ? (
      <Card elevation={0}>
        <CardContent sx={{ py: 2.2 }}>
          <Typography sx={{ fontSize: 13.5, color: "text.primary", fontWeight: 900 }}>
            No events found
          </Typography>
          <Typography sx={{ mt: 0.6, fontSize: 12.5, color: "text.secondary" }}>
            Add items inside upcomingEventsData to show events here.
          </Typography>
        </CardContent>
      </Card>
    ) : (
      upcomingTop3.map((ev) => {
        const d = ev?.dateTime ? new Date(ev.dateTime) : null;
        const dateText =
          d && !Number.isNaN(d.getTime())
            ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
            : "";
        const timeText =
          d && !Number.isNaN(d.getTime())
            ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
            : "";

        return (
          <Card
            key={ev.id}
            elevation={0}
            sx={{
              overflow: "hidden",
              cursor: "pointer",
              "&:hover": { backgroundColor: "action.hover" }
            }}
            onClick={() => navigate(`/events/${ev.id}`)}
          >
            <Box
              sx={{
                height: 170,
                backgroundImage: `url(${ev.coverImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center"
              }}
            />

            <CardContent sx={{ pt: 2.1, pb: 2.3 }}>
              <Chip size="small" label={ev.category} sx={{ mb: 1.3 }} />

              <Typography sx={{ fontSize: 14.5, color: "text.primary", fontWeight: 900 }}>
                {ev.title}
              </Typography>

              <Typography sx={{ mt: 0.7, fontSize: 12.5, color: "text.secondary" }}>
                {dateText} • {timeText}
              </Typography>
            </CardContent>
          </Card>
        );
      })
    )}
  </Box>

  {/* link style like screenshot */}
  <Typography
    onClick={() => navigate("/events")}
    sx={{
      mt: 2,
      fontSize: 13,
      fontWeight: 800,
      color: "primary.main",
      cursor: "pointer",
      width: "fit-content",
      "&:hover": { textDecoration: "underline" }
    }}
  >
    View all events
  </Typography>
</Box>


{/* Event & Activity Highlights */}
<Box sx={{ mt: 3 }}>
  <Box className="sectionHeader">
    <Typography className="sectionHeaderTitle">
      Event & Activity Highlights
    </Typography>
    <Box className="sectionHeaderLine" />
  </Box>

  <Card elevation={0}>
    <CardContent
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.1fr 1.4fr" },
        gap: 3,
        alignItems: "center"
      }}
    >
      {/* LEFT CONTENT */}
      <Box>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 900,
            color: "text.primary"
          }}
        >
          Featured: Tech Talk Series
        </Typography>

        <Typography
          sx={{
            mt: 1,
            fontSize: 13,
            lineHeight: 1.6,
            color: "text.secondary",
            maxWidth: 460
          }}
        >
          Join industry leaders for insightful discussions on emerging
          technologies and innovation in our monthly tech talk series.
        </Typography>

        <Chip
          label="Next: Jan 18"
          size="small"
          sx={{
            mt: 1.6,
            bgcolor: "action.selected",
            color: "primary.main",
            fontWeight: 800
          }}
        />
      </Box>

      {/* RIGHT IMAGE */}
      <Box
        sx={{
          height: 220,
          borderRadius: 1,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          backgroundImage:
            "url(https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1200&q=70)",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      />
    </CardContent>
  </Card>
</Box>


          
        </Box>

{/* RIGHT banners */}
{/* RIGHT banners */}
<Box
  sx={{
    display: { xs: "none", lg: "flex" },
    flexDirection: "column",
    gap: 2
  }}
>
  {mockBanners.map((b) => (
    <Card
      key={b.id}
      elevation={0}
      sx={{
        overflow: "hidden",
        bgcolor: "background.paper"
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: "action.hover"
        }}
      >
        <Box
          sx={{
            height: 320,
            borderRadius: 0,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            backgroundImage: `url(${b.image})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "contain"
          }}
        />
      </Box>

      <Box sx={{ p: 1.5, pt: 1.2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/events")}
          sx={{
            borderColor: "primary.main",
            color: "primary.main",
            fontWeight: 800,
            borderRadius: 14,
            py: 1
          }}
        >
          Learn More
        </Button>
      </Box>
    </Card>
  ))}
</Box>



      </Box>
    </DashboardLayout>
  );
}
